import Dexie, { Table } from 'dexie';

// Database rows
export type RecordingRow = {
  trackId: string;                   // local recording identifier (server trackId when known, otherwise `local-*`)
  serverTrackId?: string;            // server recording identifier (participants.localTrackId / LiveKit metadata)
  createdAt: number;
  isRecordingDone: boolean;          // set true when capture stops
  totalChunksCount: number | null;   // set when recording stops (total segments)
  lastBatchNumberFetched: number;    // helps avoid duplicate URL batch fetches (start at -1)
  uploadToken?: string;              // bearer token to call server (long-lived)
  batchSize?: number;                // server signed-URL batch size (default 50)
};

export type ChunkStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

export type ChunkRow = {
  id: string;                        // `${trackId}_${seq}`
  trackId: string;
  seq: number;                       // 0..N-1
  createdAt: number;
  // Local storage
  opfsPath?: string;                 // OPFS path where the chunk blob lives
  // Remote upload
  signedUrl: string;                 // PUT target
  status: ChunkStatus;
  attemptCount: number;              // retries so far
  lastAttemptAt?: number;
  lastError?: string;
};

class LocalRecordingDB extends Dexie {
  public recordings!: Table<RecordingRow, string>; // PK: trackId
  public chunks!: Table<ChunkRow, string>;         // PK: id

  constructor() {
    super('LocalRecordingDB');
    this.version(1).stores({
      recordings: '&trackId, createdAt, isRecordingDone, totalChunksCount, lastBatchNumberFetched',
      chunks: '&id, trackId, seq, status, createdAt',
    });
  }
}

export const LocalRecordingDBInstance = new LocalRecordingDB();

// OPFS helpers
async function getRootDir(): Promise<FileSystemDirectoryHandle> {
  // @ts-ignore
  return await navigator.storage.getDirectory();
}

export const localRecordingChunkPath = (trackId: string, seq: number): string =>
  `/local-recordings/${trackId}/${seq}.bin`;

export async function writeBlobToOPFS(path: string, blob: Blob): Promise<string> {
  const parts = path.split('/').filter(Boolean);
  const fileName = parts.pop()!;
  let dir = await getRootDir();
  for (const seg of parts) {
    dir = await dir.getDirectoryHandle(seg, { create: true });
  }
  const fh = await dir.getFileHandle(fileName, { create: true });
  const writable = await fh.createWritable();
  await blob.stream().pipeTo(writable);
  return path;
}

export async function readFileFromOPFS(path: string): Promise<File> {
  const parts = path.split('/').filter(Boolean);
  const fileName = parts.pop()!;
  let dir = await getRootDir();
  for (const seg of parts) {
    dir = await dir.getDirectoryHandle(seg, { create: false });
  }
  const fh = await dir.getFileHandle(fileName, { create: false });
  return await fh.getFile();
}

export async function deleteOPFSFile(path: string): Promise<void> {
  const parts = path.split('/').filter(Boolean);
  const fileName = parts.pop()!;
  let dir = await getRootDir();
  for (const seg of parts) {
    dir = await dir.getDirectoryHandle(seg, { create: false });
  }
  await dir.removeEntry(fileName).catch(() => {});
}

// Convenience DB operations (thin wrappers, optional)
export async function upsertRecording(row: RecordingRow): Promise<void> {
  await LocalRecordingDBInstance.recordings.put(row);
}

export async function markRecordingDone(trackId: string, totalChunksCount: number): Promise<void> {
  await LocalRecordingDBInstance.recordings.update(trackId, {
    isRecordingDone: true,
    totalChunksCount,
  });
}

export async function putChunk(row: ChunkRow): Promise<void> {
  await LocalRecordingDBInstance.chunks.put(row);
}

export async function updateChunkStatus(id: string, status: ChunkStatus, extra?: Partial<ChunkRow>): Promise<void> {
  await LocalRecordingDBInstance.chunks.update(id, { status, ...(extra ?? {}) });
}

export async function deleteChunk(id: string): Promise<void> {
  await LocalRecordingDBInstance.chunks.delete(id);
}

/**
 * Get upload progress for a specific recording track.
 * 
 * All queries are scoped to the given trackId to ensure we only count
 * chunks belonging to this specific recording (not chunks from other recordings
 * that may exist in the database).
 * 
 * Progress is calculated as: (totalChunksCount - pendingChunks) / totalChunksCount * 100
 * 
 * If recording is still in progress, we estimate based on chunks uploaded vs created so far.
 * Returns { progress: number, isComplete: boolean, totalChunks: number, uploadedChunks: number }
 */
export async function getUploadProgress(trackId: string): Promise<{
  progress: number;
  isComplete: boolean;
  totalChunks: number;
  uploadedChunks: number;
}> {
  // Validate trackId
  if (!trackId || typeof trackId !== 'string' || trackId.trim() === '') {
    console.warn('getUploadProgress called with invalid trackId:', trackId);
    return { progress: 0, isComplete: false, totalChunks: 0, uploadedChunks: 0 };
  }
  
  const recording = await LocalRecordingDBInstance.recordings.get(trackId);
  
  if (!recording) {
    // Recording row was deleted (all uploads complete) or doesn't exist
    // Check if there are any orphan chunks for this trackId
    const orphanCount = await LocalRecordingDBInstance.chunks
      .where('trackId')
      .equals(trackId)
      .count();
    
    if (orphanCount > 0) {
      // Orphan chunks exist - recording row was deleted but chunks remain
      // This shouldn't happen in normal flow, but handle it gracefully
      console.warn(`Found ${orphanCount} orphan chunks for trackId ${trackId}`);
      return { progress: 0, isComplete: false, totalChunks: orphanCount, uploadedChunks: 0 };
    }
    
    // No recording row and no chunks - complete
    return { progress: 100, isComplete: true, totalChunks: 0, uploadedChunks: 0 };
  }
  
  // Count remaining chunks in DB for THIS specific trackId only
  // (pending, uploading, or failed status - these haven't been uploaded yet)
  const pendingCount = await LocalRecordingDBInstance.chunks
    .where('trackId')
    .equals(trackId)
    .count();
  
  if (recording.isRecordingDone && recording.totalChunksCount !== null) {
    // Recording finished, we know the exact total from when it stopped
    const totalChunks = recording.totalChunksCount;
    const uploadedChunks = totalChunks - pendingCount;
    const progress = totalChunks > 0 ? (uploadedChunks / totalChunks) * 100 : 100;
    const isComplete = pendingCount === 0;
    
    return { 
      progress: Math.min(100, Math.max(0, progress)), 
      isComplete, 
      totalChunks, 
      uploadedChunks 
    };
  }
  
  // Recording still in progress - estimate based on chunks we can see
  // Query only chunks for THIS trackId (these are chunks still pending upload)
  const chunks = await LocalRecordingDBInstance.chunks
    .where('trackId')
    .equals(trackId)
    .toArray();
  
  if (chunks.length === 0) {
    // No pending chunks for this trackId
    // Either just started (nothing created yet) or all created chunks have been uploaded
    // In both cases, there's nothing pending to upload, so show 100%
    return { progress: 100, isComplete: false, totalChunks: 0, uploadedChunks: 0 };
  }
  
  // Find max seq number among pending chunks to estimate total created so far
  // (seq is 0-indexed, so maxSeq + 1 = at least this many chunks have been created)
  const maxSeq = Math.max(...chunks.map(c => c.seq));
  const estimatedTotalCreated = maxSeq + 1;
  
  // pendingCount = chunks still in DB = not yet uploaded
  // uploadedChunks = total created - still pending
  const uploadedChunks = estimatedTotalCreated - pendingCount;
  
  // Progress: what fraction of created chunks have been uploaded
  const progress = estimatedTotalCreated > 0 ? (uploadedChunks / estimatedTotalCreated) * 100 : 100;
  
  return { 
    progress: Math.min(100, Math.max(0, progress)), 
    isComplete: false, 
    totalChunks: estimatedTotalCreated, 
    uploadedChunks 
  };
}

/**
 * Returns true if there is any active local capture or any pending local-recording uploads.
 * Used for unload guards / reliability UX.
 */
export async function HasPendingLocalRecordingWork(): Promise<boolean> {
  try {
    const pendingChunks = await LocalRecordingDBInstance.chunks.count();
    if (pendingChunks > 0) return true;
    // Avoid querying boolean indexes (IDB keys don't support boolean reliably across browsers).
    const recordings = await LocalRecordingDBInstance.recordings.toArray();
    return recordings.some((r) => r?.isRecordingDone === false);
  } catch {
    return false;
  }
}
